/**
 * Full-screen branded bootstrap experience shown by AppShell while
 * Auth/Permission/Branch context is restoring (hard refresh, first load).
 * Exists specifically to close the race where the app used to render real
 * page content — including a false "Akses dibatasi" message — before
 * PermissionContext had actually finished resolving. Nothing here is a
 * demo/mock state: it is purely a loading presentation over the same real
 * bootstrap sequence (getSession -> fn_get_my_context -> fn_check_permission
 * prefetch -> branches) that was already happening, just previously
 * invisible to the user.
 */
export function BootScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        background: 'var(--alex-sidebar-bg)',
        fontFamily: 'var(--alex-font-family)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 800, fontSize: 26, letterSpacing: -0.4 }}>
          <span style={{ color: '#fff' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--alex-sidebar-text-muted)', marginTop: 4, fontWeight: 500 }}>EL Amos Web ERP</div>
      </div>
      <div style={{ display: 'flex', gap: 6 }} aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--alex-primary)',
              animation: `alex-boot-pulse 1.1s ease-in-out ${i * 0.15}s infinite`,
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--alex-sidebar-text-muted)' }} role="status">
        Menyiapkan workspace...
      </div>
      <style>{`
        @keyframes alex-boot-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

/**
 * Shown instead of BootScreen when bootstrap fails with a real network/RPC
 * error (never for "no permission" — that is a different, later state).
 * Retriable, bounded (no auto-retry loop) — the user explicitly triggers
 * another attempt.
 */
export function BootErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        background: 'var(--alex-app-bg)',
        fontFamily: 'var(--alex-font-family)',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 20 }}>
        <span style={{ color: 'var(--alex-text-primary)' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
      </div>
      <div style={{ fontSize: 32 }} aria-hidden>
        ⚠️
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--alex-text-primary)' }}>Gagal memuat sesi/izin akses</div>
      <div style={{ fontSize: 12.5, color: 'var(--alex-text-secondary)', maxWidth: 420 }}>
        Ini adalah kegagalan jaringan/server, bukan penolakan akses. {message}
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: 6,
          height: 'var(--alex-control-height)',
          padding: '0 16px',
          borderRadius: 'var(--alex-control-radius)',
          border: 'none',
          background: 'var(--alex-primary)',
          color: '#fff',
          fontWeight: 600,
          fontSize: 'var(--alex-fs-caption)',
          cursor: 'pointer',
        }}
      >
        Coba lagi
      </button>
    </div>
  );
}
